import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Trash2, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CATS = [
  { v: "escrito_judicial", l: "Escrito judicial" },
  { v: "sentencia", l: "Sentencia" },
  { v: "auto", l: "Auto" },
  { v: "informe", l: "Informe" },
  { v: "contrato", l: "Contrato" },
  { v: "plantilla", l: "Plantilla" },
  { v: "otro", l: "Otro" },
];

export default function Documents() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ category: "escrito_judicial", tags: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Selecciona un archivo");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", form.category);
      fd.append("tags", form.tags);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido y analizado");
      setOpen(false);
      fileRef.current.value = "";
      setForm({ category: "escrito_judicial", tags: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar documento?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const canDelete = user?.role === "admin" || user?.role === "abogado";
  const filtered = filter
    ? items.filter((d) => d.original_name.toLowerCase().includes(filter.toLowerCase()) || (d.category || "").includes(filter))
    : items;

  return (
    <PageContainer>
      <PageHeader
        title="Documentos"
        subtitle="Biblioteca de PDFs y documentos Word, con texto extraído listo para usar con IA."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-document-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
                <Upload className="w-4 h-4 mr-2" />Subir documento
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl">Subir documento</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Archivo (PDF, DOCX, DOC, TXT)</Label>
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" data-testid="file-input" className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#7B1826] file:text-white file:rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Etiquetas (coma)</Label>
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-sm mt-1" placeholder="urgente, civil, ..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button onClick={upload} disabled={uploading} data-testid="upload-submit-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
                  {uploading ? "Subiendo..." : "Subir"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-4">
        <Input
          placeholder="Buscar por nombre o categoría..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="doc-filter-input"
          className="rounded-sm max-w-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <FileText className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">Sin documentos.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F3F4F6] text-[11px] uppercase tracking-wider text-neutral-600">
                <th className="text-left py-3 px-4">Nombre</th>
                <th className="text-left py-3 px-4">Categoría</th>
                <th className="text-left py-3 px-4">Autor</th>
                <th className="text-left py-3 px-4">Fecha</th>
                <th className="text-right py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-t border-[#E5E7EB] hover:bg-[#FBFBF9]" data-testid={`doc-row-${d.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-neutral-400" />
                      <span className="truncate max-w-[320px]">{d.original_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4"><Badge variant="outline" className="rounded-sm text-[10px] uppercase border-[#E5E7EB]">{d.category}</Badge></td>
                  <td className="py-3 px-4 text-neutral-600">{d.uploader_name || "—"}</td>
                  <td className="py-3 px-4 text-neutral-600 font-mono text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => setPreview(d)} className="inline-flex items-center text-neutral-500 hover:text-[#7B1826] p-1.5" title="Ver extracto">
                      <Eye className="w-4 h-4" />
                    </button>
                    <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} className="inline-flex items-center text-neutral-500 hover:text-[#7B1826] p-1.5" title="Descargar">
                      <Download className="w-4 h-4" />
                    </a>
                    {canDelete && (
                      <button onClick={() => remove(d.id)} className="inline-flex items-center text-neutral-500 hover:text-[#DC2626] p-1.5" title="Eliminar">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-display text-xl truncate">{preview?.original_name}</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed bg-[#FBFBF9] p-4 border border-[#E5E7EB] font-mono text-xs">
            {preview?.extracted_text || "Sin texto extraído."}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
